import React, { useMemo, useEffect, useCallback, memo, useState } from "react";
import _ from "lodash";
import {
  getSelectedCurriculumId,
  getOrganizationId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { connect } from "react-redux";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import Loading from "UIComponents/Loading";
import { STANDARD_COLUMNS } from "AppComponents/StandardsSelectionModal/modules/Constants";
import StandardsTable from "StandardsTable";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import { useStandardsTableProps } from "StandardsTable/hooks";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import { useI18n } from "Hooks";
import SearchTextEmptyState from "./SearchTextEmptyState";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import {
  getIsParentStandardsTaggingEnabled,
  getIsStandardsV2,
} from "Post/utils";

const learningGoalsDefaultColumns = ["SELECTION", "TITLE", "CODE", "TAGS"];

const COLUMN_PROPERTIES = {
  [DATA_GRID_COLUMN_TYPES.TITLE]: {
    headerConfig: {
      width: 400,
    },
  },
  [DATA_GRID_COLUMN_TYPES.DESCRIPTION]: {
    headerConfig: {
      width: 400,
    },
  },
  [DATA_GRID_COLUMN_TYPES.GRADE_LEVELS]: {
    customConfig: {
      property: "grades",
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGS]: {
    customConfig: {
      property: "standardsWrapper.properties.tags",
      showEmptyCellAsFallback: true,
    },
  },
};
const _getEdgesFromNodeMap = ({
  nodeMap,
  selectedStandards,
  learningCourseId,
}) => {
  return Array.from(nodeMap.values(), ({ rowId, children, columns }) => {
    if (
      !_.isEmpty(children) &&
      getIsStandardsV2() &&
      !getIsParentStandardsTaggingEnabled() &&
      !learningCourseId
    ) {
      _.set(columns, "[0].isDisabled", true);
    }
    columns[0].value = getCheckboxValue({
      value: selectedStandards.has(rowId),
    });

    return {
      rowId,
      children,
      columns,
    };
  });
};

const StandardsView = memo(props => {
  const {
    edges,
    selectedStandards = [],
    setSelectedStandards,
    isLoading,
    customResetFilters,
    currentColumns,
    selectedSubject,
    learningCourseId,
  } = props;
  const subjectId = _.get(selectedSubject, "id");
  // const isCourseV2 = getIsNewGradingSetupEnabled();
  const isStandardsUIRevamped = getIsStandardsV2() && !learningCourseId;

  const { t } = useI18n(["journal", "common"]);
  // Memoized values to prevent unnecessary renders
  const [selectedRows, setSelectedRows] = useState(selectedStandards || []);
  // Stable memoized edges to prevent unnecessary re-renders
  const memoizedEdges = useMemo(() => {
    if (isStandardsUIRevamped) {
      return (
        _.map(edges, edge => ({
          node: {
            ...edge,
            parentId: edge.parent,
          },
        })) || []
      );
    }
    return edges || [];
  }, [edges]);

  useEffect(() => {
    setSelectedRows(selectedStandards);
  }, [selectedStandards]);
  // Stable callback for handling row selection
  // const handleSetSelectedRows = useCallback(
  //   rows => {
  //     setSelectedRows(rows);
  //     if (setSelectedStandards) {
  //       setSelectedStandards(rows);
  //     }
  //   },
  //   [setSelectedStandards]
  // );

  const throttledSetSelectedStandards = useCallback(
    _.throttle(
      rows => {
        if (setSelectedStandards) {
          setSelectedStandards(rows);
        }
      },
      100,
      { leading: false, trailing: true }
    ),
    [setSelectedStandards]
  );
  const handleSetSelectedRows = useCallback(
    rows => {
      // Immediately update local UI state
      setSelectedRows(rows);

      // Defer parent update to next frame and throttle rapid changes
      if (setSelectedStandards) {
        requestAnimationFrame(() => {
          throttledSetSelectedStandards(rows);
        });
      }
    },
    [setSelectedStandards, throttledSetSelectedStandards]
  );
  useEffect(() => {
    return () => {
      if (throttledSetSelectedStandards) {
        throttledSetSelectedStandards.cancel();
      }
    };
  }, [throttledSetSelectedStandards]);

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectedStandards: new Set(selectedRows || []),
        learningCourseId,
      });
    },
    [selectedRows]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      return handleSelectionChange({
        params,
        selectedRows: selectedRows || [],
        setSelectedRows: rows => handleSetSelectedRows(Array.from(rows)),
      });
    },
    [handleSetSelectedRows, selectedRows]
  );

  // Use callback for customResetFilters
  const memoizedCustomResetFilters = useCallback(() => {
    if (customResetFilters) {
      customResetFilters();
    }
  }, [customResetFilters]);

  const { columns, nodeMap, properties } = useStandardsTableProps({
    edges: memoizedEdges,
    columns: isStandardsUIRevamped
      ? learningGoalsDefaultColumns
      : STANDARD_COLUMNS,
    properties: isStandardsUIRevamped ? COLUMN_PROPERTIES : null,
  });

  if (isLoading) return <Loading />;

  return (
    <StandardsTable
      columns={isStandardsUIRevamped ? columns : currentColumns}
      nodeMap={nodeMap}
      properties={properties}
    >
      <StandardsTable.Body>
        <StandardsTable.Grid
          getEdgesFromNodeMap={getEdgesFromNodeMap}
          resetFilters={memoizedCustomResetFilters}
          onCellEdit={onCellEdit}
          emptyState={
            <SearchTextEmptyState
              t={t}
              subtitle={t("journal:please_reset_filters")}
              title={t("common:noResultHeading")}
              resetFilters={memoizedCustomResetFilters}
            />
          }
          freezeColumns={isStandardsUIRevamped ? 1 : 0}
        />
      </StandardsTable.Body>
    </StandardsTable>
  );
});

StandardsView.displayName = "StandardsView";

// Optimized props comparison for memo

const mapStateToProps = state => {
  const orgId = getOrganizationId();
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const learningCourseId = getSelectedLearningCourseId(state);

  return {
    orgId,
    curriculumProgramId,
    curriculumProgramType,
    showCourseStandards: true,
    learningCourseId,
  };
};

export default connect(mapStateToProps, null)(StandardsView);
